import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { Badge, Button } from '@wundo/ui';
import { FindingPills } from '@/components/conversationReview/FindingPills.tsx';
import { SeverityPill } from '@/components/conversationReview/SeverityPill.tsx';
import { findingClassLabel } from '@/components/conversationReview/findingClasses.ts';
import type { ConversationDebugPayload } from '@/lib/conversationDebug.ts';
import { formatMessageDateTime } from '@/utils/messageUtils.ts';

/** The sessions the analyser cut the conversation into, and what it found in each. */
export function AnalyserSessions({
  data,
  onLoadMore,
  loadingMore,
}: {
  data: ConversationDebugPayload;
  onLoadMore: () => void;
  loadingMore: boolean;
}) {
  const { items, total } = data.chat_sessions;

  if (items.length === 0) {
    return (
      <p className='text-sm text-ink-muted'>
        No chat sessions yet. Sessions form after a 4-hour gap and are skipped for
        roleplay/group conversations.
      </p>
    );
  }

  return (
    <>
      <ul className='flex flex-col gap-3'>
        {items.map(({ session, review }) => (
          <li key={session.id} className='rounded-md border border-line p-3'>
            <div className='flex items-center justify-between gap-2'>
              <span className='min-w-0 flex-1 truncate text-sm font-bold text-ink'>
                {session.title || 'Untitled session'}
              </span>
              {review ? (
                <SeverityPill severity={review.severity} />
              ) : (
                <Badge tone={session.fully_analyzed ? 'info' : 'quiet'}>
                  {session.fully_analyzed ? 'pending review' : 'not analysed'}
                </Badge>
              )}
            </div>
            <p className='mt-1 text-xs text-ink-muted'>
              messages {session.first_message_id}–{session.last_message_id} ·{' '}
              {session.message_count} msgs · started{' '}
              {formatMessageDateTime(session.started_at)}
            </p>
            {review && (
              <div className='mt-2 flex flex-col gap-1 text-xs text-ink-body'>
                <FindingPills findings={review.findings} />
                {review.findings.map((finding, index) => (
                  <p key={index}>
                    <span className='font-bold'>{findingClassLabel(finding.class)}:</span>{' '}
                    {finding.description}
                    {finding.evidence ? ` — “${finding.evidence}”` : ''}
                  </p>
                ))}
                {review.trace_id && <TraceLink traceId={review.trace_id} />}
              </div>
            )}
          </li>
        ))}
      </ul>

      {items.length < total && (
        <Button
          variant='ghost'
          size='sm'
          className='self-start'
          loading={loadingMore}
          disabled={loadingMore}
          onClick={onLoadMore}
        >
          Load more ({total - items.length} left)
        </Button>
      )}
    </>
  );
}

function TraceLink({ traceId }: { traceId: string }) {
  return (
    <Link
      to='/traces/$traceId'
      params={{ traceId }}
      target='_blank'
      className='inline-flex items-center gap-1 text-sm text-ink-secondary underline underline-offset-2 hover:text-ink'
    >
      <ExternalLink className='size-3.5' aria-hidden='true' />
      Trace
    </Link>
  );
}
